/**
 * FILE: packages/supabase/src/installments/writes.ts
 * PURPOSE: Installment plan write operations.
 */

import type { DbClient } from '../dbClient'
import { defaultDbClient } from '../dbClient'
import type { ActivityActor } from '../internal/activity'
import { logActivity, withActivityActor } from '../internal/activity'
import type { Installment, InstallmentCreatePayload, InstallmentUpdatePayload } from './dto'

function toStatus(status: Installment['status']): string {
  return status
}

export async function createInstallment(
  payload: InstallmentCreatePayload,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<Installment> {
  const { data, error } = await client
    .from('installments')
    .insert({
      phone: payload.phone,
      enrollment_id: payload.enrollmentId ?? null,
      amount: payload.amount,
      due_date: payload.dueDate,
      paid_amount: 0,
      status: 'pending',
      notes: payload.notes ?? null,
    })
    .select('id, phone, enrollment_id, amount, paid_amount, due_date, paid_at, status, notes, created_at, updated_at')
    .single()

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'installment.create',
      entityType: 'installments',
      entityId: payload.phone,
      newValues: data,
    }),
    client
  )

  return data as unknown as Installment
}

export async function updateInstallment(
  id: string,
  payload: InstallmentUpdatePayload,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<Installment> {
  const patch: {
    amount?: number
    due_date?: string
    paid_amount?: number
    status?: string
    paid_at?: string | null
    notes?: string | null
    updated_at: string
  } = { updated_at: new Date().toISOString() }
  if (payload.amount !== undefined) patch.amount = payload.amount
  if (payload.dueDate !== undefined) patch.due_date = payload.dueDate
  if (payload.paidAmount !== undefined) patch.paid_amount = payload.paidAmount
  if (payload.status !== undefined) patch.status = toStatus(payload.status)
  if (payload.paidAt !== undefined) patch.paid_at = payload.paidAt
  if (payload.notes !== undefined) patch.notes = payload.notes

  const { data, error } = await client
    .from('installments')
    .update(patch)
    .eq('id', id)
    .is('deleted_at', null)
    .select('id, phone, enrollment_id, amount, paid_amount, due_date, paid_at, status, notes, created_at, updated_at')
    .single()

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'installment.update',
      entityType: 'installments',
      entityId: id,
      newValues: data,
    }),
    client
  )

  return data as unknown as Installment
}

export async function deleteInstallment(
  id: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<void> {
  const { error } = await client
    .from('installments')
    .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', id)
    .is('deleted_at', null)

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'installment.delete',
      entityType: 'installments',
      entityId: id,
    }),
    client
  )
}

export async function markInstallmentPaid(
  id: string,
  paidAmount: number,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<Installment> {
  return updateInstallment(
    id,
    {
      paidAmount,
      status: 'paid',
      paidAt: new Date().toISOString(),
    },
    actor,
    client
  )
}

export async function waiveInstallment(
  id: string,
  actor: ActivityActor,
  client: DbClient = defaultDbClient
): Promise<Installment> {
  return updateInstallment(
    id,
    {
      status: 'waived',
      paidAt: new Date().toISOString(),
    },
    actor,
    client
  )
}
